import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import ExportButton from '../../components/ExportButton';
import { Users, Path, Package, Clock, TrendUp } from '@phosphor-icons/react';

const USAGE_COLUMNS = [
  { key: 'name', label: 'Helper' },
  { key: 'emp_id', label: 'Emp/Contract ID' },
  { key: 'trip_count', label: 'Trips' },
  { key: 'completed_count', label: 'Completed' },
  { key: 'avg_duration_min', label: 'Avg Duration (min)' },
  { key: 'avg_qty_per_trip', label: 'Avg Qty / Trip' },
  { key: 'small_qty_trip_count', label: 'Small Qty Trips (<5)' },
  { key: 'total_kms', label: 'Total KMs' },
];

function StatCard({ label, value, unit, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-[#E5E5E5] p-5 flex items-start gap-4">
      <div className={`w-10 h-10 flex items-center justify-center ${accent || 'bg-[#002FA7]/10 text-[#002FA7]'}`}>
        <Icon size={20} weight="bold" />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-widest text-[#666]">{label}</p>
        <p className="text-2xl font-black tracking-tight text-[#0A0A0A] mt-1">
          {value ?? '—'}{unit && <span className="text-sm font-medium text-[#666] ml-1">{unit}</span>}
        </p>
      </div>
    </div>
  );
}

const fmt = (v, d = 2) => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: d }) : '—';
};

export default function LoadmenUsage() {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = new Date(); monthStart.setDate(1);
  const [fromDate, setFromDate] = useState(monthStart.toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(today);
  const [data, setData] = useState({ summary: {}, breakdown: [] });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/loadmen-usage', {
        params: { from_date: fromDate, to_date: toDate },
      });
      setData(data);
    } catch {
      setData({ summary: {}, breakdown: [] });
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const { summary = {}, breakdown = [] } = data;
  const q = search.trim().toLowerCase();
  const rows = q ? breakdown.filter(b => (b.name || '').toLowerCase().includes(q) || (b.emp_id || '').toLowerCase().includes(q)) : breakdown;

  return (
    <div data-testid="loadmen-usage-page" className="space-y-6">
      <div>
        <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Helpers Usage</h1>
        <p className="text-[#666] text-sm mt-1">
          Trip-level efficiency view per loadman. Small-qty trips highlight over-staffing on light loads.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E5E5] p-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
        <div>
          <Label className="text-xs uppercase tracking-widest">From</Label>
          <Input data-testid="lu-from" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="rounded-none" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">To</Label>
          <Input data-testid="lu-to" type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="rounded-none" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-widest">Search</Label>
          <Input data-testid="lu-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Name or Emp/Contract ID" className="rounded-none" />
        </div>
        <Button data-testid="lu-refresh" onClick={fetchData} className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none">
          {loading ? 'Loading...' : 'Refresh'}
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard label="Trips with Helpers" value={fmt(summary.total_trips_with_loadmen, 0)} icon={Users} />
        <StatCard label="Trips without Helpers" value={fmt(summary.total_trips_without_loadmen, 0)} icon={Package} accent="bg-[#FF8C00]/10 text-[#FF8C00]" />
        <StatCard label="Helpers Tracked" value={fmt(summary.total_loadmen_tracked, 0)} icon={TrendUp} accent="bg-[#00C853]/10 text-[#00C853]" />
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <div className="px-4 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
          <h2 className="font-black tracking-tight text-[#0A0A0A]">Per Helper</h2>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#666]">{rows.length} {rows.length === 1 ? 'entry' : 'entries'}</span>
            <ExportButton rows={rows} columns={USAGE_COLUMNS} filename="loadmen_usage" testId="export-loadmen-usage-btn" />
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F3F3F3]">
              <TableHead className="text-xs uppercase tracking-widest">Helper</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Emp / Contract ID</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Trips</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Completed</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">
                <span className="inline-flex items-center gap-1"><Clock size={12} /> Avg Dur (min)</span>
              </TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Avg Qty</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Small Qty Trips</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">
                <span className="inline-flex items-center gap-1"><Path size={12} /> KMs</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => {
              const highlightSmall = r.small_qty_trip_count > 0;
              return (
                <TableRow key={r.key} className={i % 2 ? 'bg-[#F3F3F3]/30' : ''} data-testid={`lu-row-${r.key}`}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="font-mono text-sm">{r.emp_id || <span className="text-[#999]">—</span>}</TableCell>
                  <TableCell className="text-right">{r.trip_count}</TableCell>
                  <TableCell className="text-right">{r.completed_count}</TableCell>
                  <TableCell className="text-right">{fmt(r.avg_duration_min, 1)}</TableCell>
                  <TableCell className="text-right">{fmt(r.avg_qty_per_trip, 2)}</TableCell>
                  <TableCell className="text-right">
                    {highlightSmall ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#FF8C00]/10 text-[#FF8C00] text-xs font-semibold">
                        {r.small_qty_trip_count}
                      </span>
                    ) : (
                      <span className="text-[#999]">0</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">{fmt(r.total_kms, 2)}</TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-[#666] py-8">
                {loading ? 'Loading...' : 'No helper usage data in this range'}
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <p className="text-[10px] text-[#666]">
        Duration = accepted_at → completed_at. Small qty threshold = total items qty &lt; 5. Trips without helpers are aggregated under "(No Helper)" to show how much work happens un-assisted.
      </p>
    </div>
  );
}
